import { Box, Button, ButtonBase, Grid } from "@mui/material";
import React from "react";
import { tokens } from "../../../theme";

/**
 * The grey-pill toggle group every analytics card grew by hand — one
 * component now, keyed on `tokens.cardChrome.variant` like SectionCard.
 *
 *  - "banner" (legacy): the original markup verbatim from tokens — grey
 *    group (2px pad, 10px radius), 5px-padded cells, outlined Buttons with
 *    the active segment filled. `size="sm"` is the Growth & Forecast
 *    retention group's 10px labels; `preserveLabelCase` is Sales & Marketing's
 *    capitalize override.
 *  - "plain" (refreshed): an inset-chip group per the sidebar-mui preview
 *    conventions — pill container, 13px/500 chip segments, the active one
 *    lifted on controlActiveBg with a hairline border.
 *
 * Strictly a controlled exclusive choice: options/value/onChange. Single
 * always-active action pills (the Download buttons) are NOT this component.
 */
export interface SegmentedOption<T extends string = string> {
  value: T;
  label: React.ReactNode;
}

export interface SegmentedControlProps<T extends string = string> {
  /** 2–4 options. */
  options: SegmentedOption<T>[];
  value: T;
  onChange: (value: T) => void;
  /** "sm" = the 10px-label variant. Default "md". */
  size?: "md" | "sm";
  /** Don't uppercase labels (legacy renders textTransform: capitalize). */
  preserveLabelCase?: boolean;
}

function SegmentedControl<T extends string = string>({
  options,
  value,
  onChange,
  size = "md",
  preserveLabelCase = false,
}: SegmentedControlProps<T>) {
  if (tokens.cardChrome.variant === "banner") {
    return (
      <Grid
        sx={{
          backgroundColor: tokens.cardChrome.controlGroupBg,
          display: "flex",
          padding: "2px",
          borderRadius: "10px",
        }}
      >
        {options.map((opt) => (
          <Grid key={opt.value} sx={{ justifyContent: "center", padding: "5px" }}>
            <Button
              onClick={() => onChange(opt.value)}
              variant="outlined"
              sx={{
                color: tokens.cardChrome.controlText,
                padding: "0px 20px 0px 20px",
                backgroundColor:
                  value === opt.value
                    ? tokens.cardChrome.controlActiveBg
                    : tokens.cardChrome.controlGroupBg,
                ...(size === "sm" && { fontSize: "10px" }),
                ...(preserveLabelCase && { textTransform: "capitalize" }),
              }}
            >
              {opt.label}
            </Button>
          </Grid>
        ))}
      </Grid>
    );
  }

  return (
    <Box
      sx={{
        display: "inline-flex",
        backgroundColor: tokens.cardChrome.controlGroupBg,
        borderRadius: 999,
        padding: "3px",
        gap: "2px",
      }}
    >
      {options.map((opt) => {
        const active = value === opt.value;
        return (
          <ButtonBase
            key={opt.value}
            onClick={() => onChange(opt.value)}
            sx={{
              fontFamily: "inherit",
              fontSize: size === "sm" ? 12 : 13,
              fontWeight: 500,
              lineHeight: 1,
              padding: "6px 12px",
              borderRadius: 999,
              whiteSpace: "nowrap",
              color: active ? tokens.cardChrome.controlText : tokens.color.muted,
              backgroundColor: active ? tokens.cardChrome.controlActiveBg : "transparent",
              border: `1px solid ${active ? tokens.color.border : "transparent"}`,
              transition: `background-color ${tokens.motion.fast} ${tokens.motion.easing}, color ${tokens.motion.fast} ${tokens.motion.easing}`,
            }}
          >
            {opt.label}
          </ButtonBase>
        );
      })}
    </Box>
  );
}

export default SegmentedControl;
